import React, { PropsWithChildren, useRef, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Slider,
  Box,
  Typography,
} from "@mui/material";
import Cropper from "react-easy-crop";
import { IconCamera } from "@tabler/icons-react";
import { Area } from "react-easy-crop/types";
import { radius } from "../../../theme";

export default function UploadAvatar({
  disabled = false,
  children,
  onSave,
}: PropsWithChildren<{
  onSave: (canvas: HTMLCanvasElement) => void;
  disabled?: boolean;
}>) {
  const [image, setImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area
  ): Promise<HTMLCanvasElement> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return canvas;
  };

  const handleSave = async () => {
    if (!image || !croppedAreaPixels) return;

    try {
      const canvas = await getCroppedImg(image, croppedAreaPixels);
      onSave(canvas);
      setImage(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (e) {
      console.error("Erro ao recortar imagem:", e);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleUpload}
        hidden
        disabled={disabled}
      />
      <Badge
        color="primary"
        badgeContent={<IconCamera size={20} />}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        onClick={() => inputRef.current?.click()}
        invisible={disabled}
      >
        {children}
      </Badge>
      {!!image && !disabled && (
        <Dialog open={!!image} maxWidth="sm" fullWidth>
          <DialogContent>
            <Box
              sx={{
                position: "relative",
                height: 300,
                width: "100%",
                // Painel de recorte com o radius de painel e fill sutil de tinta.
                borderRadius: `${radius.xl}px`,
                overflow: "hidden",
                bgcolor: "action.hover",
              }}
            >
              <Cropper
                image={image}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="round"
                showGrid={false}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              {/* Rótulo de campo em mono UPPERCASE tracked. */}
              <Typography
                variant="labelMono"
                component="p"
                color="text.secondary"
                gutterBottom
              >
                Zoom
              </Typography>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(_, value) => setZoom(value as number)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                setImage(null);
                setCrop({ x: 0, y: 0 });
                setZoom(1);
              }}
            >
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
